<template>
    <div class="object-info" :class="{ active: open }">
        <div class="menu">
            <font-awesome-icon
                icon="times"
                class="fa-lg dismiss-icon"
                @click="$emit('object-info:dismiss')"
            />
            <Tooltip
                v-if="
                    value.validations && value.validations.some((v) => v.status.name === 'waiting')
                "
                tooltip="En attente"
                :tooltip-text="value.validations.find((v) => v.status.name === 'waiting').comment"
                position="bottom"
                class="waiting-tooltip"
            />
            <div class="object-actions">
                <font-awesome-icon
                    title="Valider"
                    icon="check"
                    class="actions--icon-accept"
                    @click="$emit('object-info:accept')"
                />
                <font-awesome-icon
                    v-if="
                        value.validations &&
                            !value.validations.some((v) => v.status.name === 'waiting')
                    "
                    title="Mettre en attente"
                    icon="clock"
                    class="inverse actions--icon-waiting"
                    @click="$emit('object-info:waiting')"
                />
                <font-awesome-icon
                    title="Rejeter"
                    icon="pen"
                    class="actions--icon-reject"
                    @click="$emit('object-info:reject')"
                />
            </div>
        </div>
        <div class="scroller">
            <div class="section">
                <div class="header">Informations</div>
                <div class="body">
                    <div class="row">
                        <div class="col">Identifiant</div>
                        <div class="col">
                            <a
                                class="link"
                                :href="`https://www.openstreetmap.org/${value.element}/${value.id}`"
                                target="_blank"
                                >{{ value.id }}</a
                            >
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">Date</div>
                        <div class="col">{{ formatDate(value.new_timestamp) }}</div>
                    </div>
                    <div class="row">
                        <div class="col">Version</div>
                        <div class="col">{{ value.version }}</div>
                    </div>
                    <div class="row">
                        <div class="col">Contributeur</div>
                        <div v-if="contributor.vigilance" class="col">
                            <a
                                class="link-contrib"
                                :href="`https://www.openstreetmap.org/user/${contributor.name}`"
                                target="_blank"
                                :style="{ color: contributor.vigilance.color }"
                                >{{ contributor.name }}
                            </a>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col">Statut</div>
                        <div class="col" v-if="value.action === 'modify'">Objet modifié</div>
                        <div class="col" v-if="value.action === 'create'">Objet créé</div>
                        <div class="col" v-if="value.action === 'delete'">Objet supprimé</div>
                    </div>

                    <div class="row" :class="{ 'active-key': checkGeom && value.changed_geom }">
                        <div class="col">Géométrie</div>
                        <div class="col" v-if="value.action === 'create'">créée</div>
                        <div class="col" v-if="value.action === 'delete'">supprimée</div>
                        <div class="col" v-if="value.action === 'modify'">
                            {{ value.changed_geom ? '' : 'non ' }}modifiée
                        </div>
                    </div>

                    <div class="row">
                        <div class="col">Type</div>
                        <div class="col">{{ value.element }}</div>
                    </div>
                </div>
            </div>
            <div class="section">
                <div class="header">Groupe de modifications</div>
                <div class="body">
                    <div class="row">
                        <div class="col">Identifiant</div>
                        <div class="col">
                            <a
                                class="link"
                                :href="
                                    `https://www.openstreetmap.org/changeset/${
                                        value.od_new_changeset
                                    }`
                                "
                                target="_blank"
                                >{{ value.od_new_changeset }}</a
                            >
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">Commentaire</div>
                        <div class="col">
                            {{
                                value.new_changeset &&
                                    value.new_changeset.tags &&
                                    value.new_changeset.tags.comment
                            }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="section" v-if="value.validations && value.validations.length">
                <div class="header history-header" @click="toggleHistory">
                    Validation partielle
                    <font-awesome-icon
                        icon="angle-down"
                        class="history-toggle"
                        :class="{ active: isHistoryOpen }"
                    />
                </div>
                <div class="body history-body" v-show="isHistoryOpen">
                    <div
                        class="history-row"
                        v-for="validation in value.validations"
                        :key="validation.id"
                    >
                        <div>
                            <font-awesome-icon
                                v-if="validation.status.name === 'waiting'"
                                icon="clock"
                            />
                            <font-awesome-icon
                                v-if="validation.status.name === 'valid'"
                                icon="check"
                            />
                            {{ `${validation.user.first_name} ${validation.user.last_name}` }} le
                            {{ formatDate(validation.val_created_at) }}
                        </div>
                        <div v-if="validation.status.name === 'valid'">
                            {{ validationTagsLabel(validation) }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="section" v-if="Object.keys(value.added_tags || {}).length">
                <div class="header">Attributs ajoutés</div>
                <div class="body" id="panneau-attributs-ajoutes">
                    <div
                        v-for="tag in Object.entries(value.added_tags || {})"
                        :key="tag[0]"
                        class="row"
                        :class="{ 'active-key': isToValidate(tag[0]) || checkAllKeys }"
                    >
                        <div class="col">{{ tag[0] }}</div>
                        <div class="col">{{ tag[1] }}</div>
                    </div>
                </div>
            </div>
            <div class="section" v-if="Object.keys(value.modified_tags || {}).length">
                <div class="header">Attributs modifiés</div>
                <div class="body" id="panneau-attributs-modifies">
                    <div
                        v-for="tag in Object.entries(value.modified_tags || {})"
                        :key="tag[0]"
                        class="row"
                        :class="{ 'active-key': isToValidate(tag[0]) || checkAllKeys }"
                    >
                        <div class="col">{{ tag[0] }}</div>
                        <div class="col">
                            <span class="old-value"> {{ value.old_tags[tag[0]] }} </span>
                            {{ tag[1] }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="section" v-if="Object.keys(value.deleted_tags || {}).length">
                <div class="header">Attributs supprimés</div>
                <div class="body" id="panneau-attributs-supprimes">
                    <div
                        v-for="tag in Object.entries(value.deleted_tags || {})"
                        :key="tag[0]"
                        class="row"
                        :class="{ 'active-key': isToValidate(tag[0]) || checkAllKeys }"
                    >
                        <div class="col">{{ tag[0] }}</div>
                        <div class="col deleted">{{ tag[1] }}</div>
                    </div>
                </div>
            </div>
            <div class="section" v-if="Object.keys(value.unchanged_tags || {}).length">
                <div class="header">Attributs inchangés</div>
                <div class="body" id="panneau-attributs-inchanges">
                    <div
                        v-for="tag in Object.entries(value.unchanged_tags || {})"
                        :key="tag[0]"
                        class="row"
                    >
                        <div class="col">{{ tag[0] }}</div>
                        <div class="col">{{ tag[1] }}</div>
                    </div>
                </div>
            </div>
            <div class="section" v-if="value.od_mapillary && value.od_mapillary.length">
                <div class="header">Photo Mapillary</div>
                <div class="body">
                    <a
                        class="link"
                        :href="`https://www.mapillary.com/map/im/${value.od_mapillary}`"
                        target="_blank"
                    >
                        <img
                            :src="
                                `https://images.mapillary.com/${value.od_mapillary}/thumb-320.jpg`
                            "
                            alt="Image Mapillary"
                            class="mapillary-img"
                        />
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Tooltip from '@/components/Tooltip';

export default {
    props: {
        checkGeom: Boolean,
        checkAllKeys: Boolean,
        open: Boolean,
        object: Object,
        keys: Array,
    },
    data() {
        return {
            isHistoryOpen: false,
        };
    },
    computed: {
        value() {
            return this.object || {};
        },
        contributor() {
            return (this.object && this.object.contributor) || {};
        },
        isGeomChanged() {
            if (!this.object) {
                return false;
            }
            const { new_tags, old_tags, changed_geom } = this.object;
            return (
                !(!!Object.keys(new_tags || {}).length ^ !!Object.keys(old_tags || {}).length) &&
                changed_geom
            );
        },
    },
    methods: {
        formatDate(date) {
            if (date) {
                return new Date(date).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                });
            }
            return '';
        },
        toggleHistory() {
            this.isHistoryOpen = !this.isHistoryOpen;
        },
        isToValidate(tag) {
            return (
                this.keys.some((k) => k === tag) &&
                !this.object.validations
                    .filter((v) => v.status.name === 'valid')
                    .some((v) => v.tags && v.tags.includes(tag))
            );
        },
        validationTagsLabel(validation) {
            if (!validation.tags || !validation.tags.length) {
                return '';
            }
            return validation.tags.length > 1
                ? `Attributs : ${validation.tags.join(', ')}`
                : `Attribut : ${validation.tags.join(', ')}`;
        },
    },
    components: {
        Tooltip,
    },
};
</script>

<style lang="scss">
$menu-width: 240px;
$menu-height: 50px;

.waiting-tooltip {
    text-transform: uppercase;
    color: #6c3e91;
}

.object-info {
    position: fixed;
    z-index: 1100;
    top: 0;
    left: 0;
    width: $menu-width;
    height: 100%;
    background-color: #fafafaed;
    transition: all 0.3s ease;

    &.active {
        left: $menu-width;
        transition: all 0.3s ease;
        border-radius: 0 30px 0 0;
    }

    .menu {
        height: $menu-height;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .dismiss-icon {
            color: #2a2a2a;
            padding: 15px;
        }

        .object-actions {
            display: flex;
            margin: 15px;

            .actions--icon-accept {
                color: #ffffff;
                background-color: #93c11f;
                border-radius: 3px;
                padding: 5px;
                margin-right: 5px;
            }

            .actions--icon-waiting {
                color: #ffffff;
                background-color: #6c3e91;
                border-radius: 3px;
                padding: 5px;
                margin-right: 5px;
            }

            .actions--icon-reject {
                color: #ffffff;
                background-color: #ea5b0c;
                border-radius: 3px;
                padding: 5px;
                margin-right: 5px;
            }
        }
    }

    .scroller {
        position: absolute;
        top: $menu-height;
        bottom: 0;
        overflow-y: scroll;
        width: 100%;
    }

    .section {
        .header {
            height: 40px;
            background-color: rgba(212, 216, 221, 0.2); // #d4d8dd;
            display: flex;
            align-items: center;
            padding-left: 22px;
            color: #2a2a2a;
            text-transform: uppercase;
            font-weight: bold;
        }

        .body {
            .row {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                padding: 5px 10px 5px 10px;
                margin: 2px 10px 2px 10px;
                align-items: center;
                flex-wrap: wrap;

                &.active-key {
                    /* color: #6c3e91; */
                    /* font-size: 13px; */
                    /* font-size: 600; */
                    /* text-decoration: underline; */
                    border-style: solid;
                    border-color: #6c3e91;
                    border-width: 2px;
                    border-radius: 10px;
                }

                .col {
                    display: flex;
                    flex-direction: column;
                    word-break: break-word;

                    &:first-child {
                        font-size: 13px;
                        font-weight: 600;
                        margin-right: 15px;
                    }

                    &:last-child {
                        /* text-align: right; */
                    }

                    &.deleted {
                        text-decoration: line-through;
                    }

                    .link {
                        text-decoration: none;
                        color: #6c3e91;
                        font-weight: bolder;
                    }
                    .link-contrib {
                        text-decoration: none;
                    }
                    span.old-value {
                        text-decoration: line-through;
                        // margin-right: 1em;
                    }
                }
            }
        }
    }
}

.mapillary-img {
    max-width: 100%;
}

.history-header {
    padding-right: 18px;
    cursor: pointer;
    margin-bottom: 4px;

    .history-toggle {
        color: #2a2a2a;
        transition: transform 0.25s ease;
        transform: rotate(-90deg);
        height: 20px;
        font-size: 20px;
        width: 14px;
        margin-left: auto;

        &.active {
            transform: rotate(0deg);
        }
    }
}
.history-body {
    border-bottom: 1px solid #d4d8dd;
    opacity: 0.5;

    .history-row {
        padding: 10px 20px;
    }
}

/*
#panneau-attributs-ajoutes {
    background-color: rgba(250, 247, 151, 0.5);
}
#panneau-attributs-supprimes {
    background-color: rgba(255, 51, 51, 0.5);
}
#panneau-attributs-modifies {
    background-color: rgba(135, 206, 250, 0.5);
}
#panneau-attributs-inchanges {
    background-color: rgba(144, 238, 144, 0.5);
}
*/
</style>
